import { useEffect, useRef, useState } from "react";

import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";

const ATOM_SIZE = 60;

const sidebarElements = [
  { type: "carbon", label: "C", text: "C", width: ATOM_SIZE, height: ATOM_SIZE, spokes: 4, color: "#111827" },
  { type: "oxygen", label: "O", text: "O", width: ATOM_SIZE, height: ATOM_SIZE, spokes: 2, color: "#16a34a" },
  { type: "hydrogen", label: "H", text: "H", width: ATOM_SIZE, height: ATOM_SIZE, spokes: 1, color: "#92400e" },
  { type: "nitrogen", label: "N", text: "N", width: ATOM_SIZE, height: ATOM_SIZE, spokes: 3, color: "#2563eb" },
  { type: "chlorine", label: "Cl", text: "Cl", width: ATOM_SIZE, height: ATOM_SIZE, spokes: 1, color: "#e3f30b" },
];

const CONNECT_DISTANCE = 55;
const BOND_LENGTH = 55;

const spokeAngles = {
  1: [0],
  2: [0, 180],
  3: [-90, 30, 150],
  4: [-90, 0, 90, 180],
};

const CANVAS_WIDTH = 2200;
const CANVAS_HEIGHT = 1600;

function getCenter(item) {
  return {
    x: item.x + item.width / 2,
    y: item.y + item.height / 2,
  };
}

function getDistance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function angleToVector(angle) {
  const radians = (angle * Math.PI) / 180;

  return {
    x: Math.cos(radians),
    y: Math.sin(radians),
  };
}

function getUsedParentAngles(itemId, bonds) {
  return bonds
    .filter((bond) => bond.parentId === itemId)
    .map((bond) => bond.parentAngle);
}

function getUsedChildAngles(itemId, bonds) {
  return bonds
    .filter((bond) => bond.childId === itemId)
    .map((bond) => bond.childAngle);
}

function getVisibleAngles(item, bonds) {
  const angles = spokeAngles[item.spokes] || [];
  const usedParentAngles = getUsedParentAngles(item.id, bonds);
  const usedChildAngles = getUsedChildAngles(item.id, bonds);

  return angles.filter(
    (angle) =>
      !usedParentAngles.includes(angle) && !usedChildAngles.includes(angle)
  );
}

function getFirstFreeAngle(item, bonds) {
  return getVisibleAngles(item, bonds)[0];
}



function AtomNode({ item, bonds }) {
  const visibleAngles = item.id
    ? getVisibleAngles(item, bonds)
    : spokeAngles[item.spokes] || [];

  return (
    <div style={styles.atomWrap}>
      {visibleAngles.map((angle) => (
        <div
          key={angle}
          style={{
            ...styles.spoke,
            transform: `rotate(${angle}deg) translateX(30px)`,
          }}
        />
      ))}

      <div
        style={{
          ...styles.atomCircle,
          borderColor: item.color,
          color: item.color,
        }}
      >
        {item.text}
      </div>
    </div>
  );
}

function getBondPairKey(bond) {
  return [bond.parentId, bond.childId].sort().join("__");
}

function getParallelOffsets(count) {
  if (count === 1) return [0];
  if (count === 2) return [-5, 5];
  if (count === 3) return [-8, 0, 8];

  return Array.from({ length: count }, (_, index) => {
    return (index - (count - 1) / 2) * 6;
  });
}

function groupBondsByPair(bonds) {
  const groups = new Map();

  bonds.forEach((bond) => {
    const key = getBondPairKey(bond);
    const group = groups.get(key) || [];
    group.push(bond);
    groups.set(key, group);
  });

  return Array.from(groups.values());
}

function CompactMoleculeNode({ item }) {
  return <div style={styles.condensedFormula}>{item.label}</div>;
}

export default function DragDropPalette() {
  const paletteRef = useRef(null);
const [items, setItems] = useState([]);
const [bonds, setBonds] = useState([]);
const [history, setHistory] = useState([]);
const [future, setFuture] = useState([]);

  const [dragging, setDragging] = useState(null);
  const [viewMode, setViewMode] = useState("expanded");

  const [carbonChainLength, setCarbonChainLength] = useState(4);
  const [moleculeName, setMoleculeName] = useState("");

  const [isGeneratingMolecule, setIsGeneratingMolecule] = useState(false);
const [moleculeError, setMoleculeError] = useState("");

  function getCanvasSize() {
  const padding = 240;

  if (items.length === 0) {
    return {
      width: "100%",
      height: "100%",
    };
  }

  const maxRight = Math.max(...items.map((item) => item.x + item.width));
  const maxBottom = Math.max(...items.map((item) => item.y + item.height));

  return {
    width: Math.max(maxRight + padding, 900),
    height: Math.max(maxBottom + padding, 600),
  };
}

  function getCurrentState() {
  return {
    items,
    bonds,
  };
}

function commitState(nextItems, nextBonds) {
  setHistory((current) => [...current, getCurrentState()]);
  setFuture([]);

  setItems(nextItems);
  setBonds(nextBonds);
}

function removeAtom(atomId) {
  const nextItems = items.filter((item) => item.id !== atomId);

  const nextBonds = bonds.filter(
    (bond) => bond.parentId !== atomId && bond.childId !== atomId
  );

  commitState(nextItems, nextBonds);
}

function undoState() {
  setHistory((currentHistory) => {
    if (currentHistory.length === 0) return currentHistory;

    const previousState = currentHistory[currentHistory.length - 1];

    setFuture((currentFuture) => [getCurrentState(), ...currentFuture]);
    setItems(previousState.items);
    setBonds(previousState.bonds);

    return currentHistory.slice(0, -1);
  });
}

function redoState() {
  setFuture((currentFuture) => {
    if (currentFuture.length === 0) return currentFuture;

    const nextState = currentFuture[0];

    setHistory((currentHistory) => [...currentHistory, getCurrentState()]);
    setItems(nextState.items);
    setBonds(nextState.bonds);

    return currentFuture.slice(1);
  });
}

function resetPalette() {
  setViewMode("expanded");
  commitState([], []);
}

  function startSidebarDrag(event, element) {
    event.preventDefault();

    setDragging({
      source: "sidebar",
      element,
      pointerX: event.clientX,
      pointerY: event.clientY,
      offsetX: element.width / 2,
      offsetY: element.height / 2,
    });
  }

function startItemDrag(event, item) {
  event.preventDefault();
  event.stopPropagation();

  if (!paletteRef.current) return;

  const palette = paletteRef.current;
  const paletteRect = palette.getBoundingClientRect();

  if (viewMode === "compact") {
    setDragging({
      source: "compact",
      item,
      pointerX: event.clientX,
      pointerY: event.clientY,
      offsetX: event.clientX - paletteRect.left + palette.scrollLeft - item.x,
      offsetY: event.clientY - paletteRect.top + palette.scrollTop - item.y,
    });

    return;
  }

  setDragging({
    source: "palette",
    item,
    pointerX: event.clientX,
    pointerY: event.clientY,
    offsetX: event.clientX - paletteRect.left + palette.scrollLeft - item.x,
    offsetY: event.clientY - paletteRect.top + palette.scrollTop - item.y,
  });
}

        function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getElementTemplate(type) {
  return sidebarElements.find((element) => element.type === type);
}

function getNextMoleculeStartX(extraGap = 220) {
  if (!paletteRef.current) return 120;

  const palette = paletteRef.current;

  if (items.length === 0) {
    return palette.scrollLeft + palette.clientWidth / 2;
  }

  const maxRight = Math.max(...items.map((item) => item.x + item.width));

  return maxRight + extraGap;
}

function addCarbonChain() {
  if (!paletteRef.current) return;

  const carbon = getElementTemplate("carbon");
  const hydrogen = getElementTemplate("hydrogen");
  if (!carbon || !hydrogen) return;

  const count = clamp(Number(carbonChainLength) || 1, 1, 10);
  const palette = paletteRef.current;
  const spacing = 88;
  const chainWidth = (count - 1) * spacing;

  const firstCenterX =
    items.length === 0
      ? Math.max(
          120,
          palette.scrollLeft + palette.clientWidth / 2 - chainWidth / 2
        )
      : getNextMoleculeStartX(160);

  const centerY = Math.max(
    220,
    palette.scrollTop + palette.clientHeight / 2
  );

  const carbonItems = Array.from({ length: count }, (_, index) => ({
    id: crypto.randomUUID(),
    ...carbon,
    x: firstCenterX + index * spacing - carbon.width / 2,
    y: centerY - carbon.height / 2,
  }));

  const carbonBonds = carbonItems.slice(1).map((item, index) => ({
    id: crypto.randomUUID(),
    parentId: carbonItems[index].id,
    childId: item.id,
    parentAngle: 0,
    childAngle: 180,
  }));

  const hydrogenDistance = 88;
  const hydrogenItems = [];
  const hydrogenBonds = [];

  carbonItems.forEach((carbonItem) => {
    getVisibleAngles(carbonItem, carbonBonds).forEach((parentAngle) => {
      const vector = angleToVector(parentAngle);
      const carbonCenter = getCenter(carbonItem);

      const hydrogenItem = {
        id: crypto.randomUUID(),
        ...hydrogen,
        x: Math.max(
          0,
          carbonCenter.x + vector.x * hydrogenDistance - hydrogen.width / 2
        ),
        y: Math.max(
          0,
          carbonCenter.y + vector.y * hydrogenDistance - hydrogen.height / 2
        ),
      };

      hydrogenItems.push(hydrogenItem);

      hydrogenBonds.push({
        id: crypto.randomUUID(),
        parentId: carbonItem.id,
        childId: hydrogenItem.id,
        parentAngle,
        childAngle: 0,
      });
    });
  });

  setViewMode("expanded");
  commitState(
    [...items, ...carbonItems, ...hydrogenItems],
    [...bonds, ...carbonBonds, ...hydrogenBonds]
  );
}



function addBenzeneMolecule() {
  if (!paletteRef.current) return;

  const carbon = getElementTemplate("carbon");
  const hydrogen = getElementTemplate("hydrogen");
  if (!carbon || !hydrogen) return;

  const palette = paletteRef.current;
const centerY = palette.scrollTop + Math.max(palette.clientHeight / 2, 360);

const carbonRadius = 84;
const hydrogenRadius = 145;
const moleculeId = crypto.randomUUID();

const centerX =
  items.length === 0
    ? palette.scrollLeft + palette.clientWidth / 2
    : getNextMoleculeStartX(hydrogenRadius + 120);

  const carbonItems = Array.from({ length: 6 }, (_, index) => {
    const angle = (index / 6) * Math.PI * 2 - Math.PI / 2;

    return {
      id: crypto.randomUUID(),
      ...carbon,
      x: centerX + Math.cos(angle) * carbonRadius - carbon.width / 2,
      y: centerY + Math.sin(angle) * carbonRadius - carbon.height / 2,
      benzeneIndex: index,
      moleculeId,
      moleculeType: "benzene",
    };
  });

  const hydrogenItems = Array.from({ length: 6 }, (_, index) => {
    const angle = (index / 6) * Math.PI * 2 - Math.PI / 2;

    return {
      id: crypto.randomUUID(),
      ...hydrogen,
      x: centerX + Math.cos(angle) * hydrogenRadius - hydrogen.width / 2,
      y: centerY + Math.sin(angle) * hydrogenRadius - hydrogen.height / 2,
      benzeneIndex: index,
      moleculeId,
      moleculeType: "benzene",
    };
  });

  const carbonSpokes = [-90, 0, 90, 180];
const usedCarbonSpokes = new Map();

carbonItems.forEach((carbonItem) => {
  usedCarbonSpokes.set(carbonItem.id, []);
});

function takeCarbonSpoke(carbonId) {
  const used = usedCarbonSpokes.get(carbonId);
  const free = carbonSpokes.find((angle) => !used.includes(angle));

  if (free === undefined) return undefined;

  used.push(free);
  return free;
}

const ringBonds = [];

for (let index = 0; index < 6; index += 1) {
  const parent = carbonItems[index];
  const child = carbonItems[(index + 1) % 6];
  const isDoubleBond = index % 2 === 0;
  const bondCount = isDoubleBond ? 2 : 1;

  for (let bondIndex = 0; bondIndex < bondCount; bondIndex += 1) {
    const parentAngle = takeCarbonSpoke(parent.id);
    const childAngle = takeCarbonSpoke(child.id);

    if (parentAngle === undefined || childAngle === undefined) continue;

    ringBonds.push({
      id: crypto.randomUUID(),
      parentId: parent.id,
      childId: child.id,
      parentAngle,
      childAngle,
    });
  }
}

const hydrogenBonds = hydrogenItems
  .map((hydrogenItem, index) => {
    const parentAngle = takeCarbonSpoke(carbonItems[index].id);

    if (parentAngle === undefined) return null;

    return {
      id: crypto.randomUUID(),
      parentId: carbonItems[index].id,
      childId: hydrogenItem.id,
      parentAngle,
      childAngle: 0,
    };
  })
  .filter(Boolean);

  setViewMode("expanded");
  commitState(
    [...items, ...carbonItems, ...hydrogenItems],
    [...bonds, ...ringBonds, ...hydrogenBonds]
  );
}

function getRootItems(allItems, allBonds) {
  const childIds = new Set(allBonds.map((bond) => bond.childId));
  const roots = allItems.filter((item) => !childIds.has(item.id));

  return roots.length > 0 ? roots : allItems.slice(0, 1);
}

function countDescendants(parentId, allBonds, visited = new Set()) {
  const childBonds = allBonds.filter((bond) => bond.parentId === parentId);

  return childBonds.reduce((total, bond) => {
    if (visited.has(bond.childId)) return total;

    visited.add(bond.childId);

    return total + 1 + countDescendants(bond.childId, allBonds, visited);
  }, 0);
}

function getMolecularFormula(groupItems) {
  const counts = {};

  groupItems.forEach((item) => {
    const symbol = item.text || item.label;
    counts[symbol] = (counts[symbol] || 0) + 1;
  });

  const orderedSymbols = [
    "C",
    "H",
    ...Object.keys(counts)
      .filter((symbol) => symbol !== "C" && symbol !== "H")
      .sort(),
  ];

  return orderedSymbols
    .filter((symbol) => counts[symbol])
    .map((symbol) => `${symbol}${counts[symbol] === 1 ? "" : counts[symbol]}`)
    .join("");
}
function getCompactItems() {
  const summaries = [];
  const coveredIds = new Set();

  function getBox(groupItems) {
    const left = Math.min(...groupItems.map((item) => item.x));
    const top = Math.min(...groupItems.map((item) => item.y));
    const right = Math.max(...groupItems.map((item) => item.x + item.width));
    const bottom = Math.max(...groupItems.map((item) => item.y + item.height));

    return {
      centerX: (left + right) / 2,
      centerY: (top + bottom) / 2,
    };
  }

  const benzeneGroups = new Map();

  items
    .filter((item) => item.moleculeType === "benzene" || item.benzeneIndex !== undefined)
    .forEach((item) => {
      const key = item.moleculeId || item.id;
      const group = benzeneGroups.get(key) || [];
      group.push(item);
      benzeneGroups.set(key, group);
    });

  benzeneGroups.forEach((group, key) => {
    group.forEach((item) => coveredIds.add(item.id));

    const box = getBox(group);
    const width = 120;
    const height = 90;

function getHexagonPoint(index, count = 6) {
  const angle = (index / count) * Math.PI * 2 - Math.PI / 2;

  return {
    x: 50 + Math.cos(angle) * 44,
    y: 50 + Math.sin(angle) * 44,
  };
}

const ringSize = 62;
const ringLeft = (width - ringSize) / 2;
const ringTop = (height - ringSize) / 2;

const sourcePoints = group.map((item) => {
  const anchor = getHexagonPoint(item.benzeneIndex);

  return {
    id: item.id,
    x: ringLeft + (anchor.x / 100) * ringSize,
    y: ringTop + (anchor.y / 100) * ringSize,
  };
});

summaries.push({
  id: `compact-benzene-${key}`,
  compactType: "benzene",
  text: getMolecularFormula(group),
  label: getMolecularFormula(group),
  color: "#111827",
  width,
  height,
  x: Math.max(0, box.centerX - width / 2),
  y: Math.max(0, box.centerY - height / 2),
  sourceIds: group.map((item) => item.id),
  sourcePoints,
});
  });

  const remainingItems = items.filter((item) => !coveredIds.has(item.id));
  const remainingIds = new Set(remainingItems.map((item) => item.id));
  const remainingBonds = bonds.filter(
    (bond) => remainingIds.has(bond.parentId) && remainingIds.has(bond.childId)
  );

  function getConnectedComponents(sourceItems, sourceBonds) {
    const itemById = new Map(sourceItems.map((item) => [item.id, item]));
    const seen = new Set();

    return sourceItems
      .map((startItem) => {
        if (seen.has(startItem.id)) return null;

        const ids = new Set([startItem.id]);
        const queue = [startItem.id];
        seen.add(startItem.id);

        while (queue.length > 0) {
          const currentId = queue.shift();

          sourceBonds.forEach((bond) => {
            const neighborId =
              bond.parentId === currentId
                ? bond.childId
                : bond.childId === currentId
                ? bond.parentId
                : null;

            if (neighborId && !seen.has(neighborId)) {
              seen.add(neighborId);
              ids.add(neighborId);
              queue.push(neighborId);
            }
          });
        }

        return Array.from(ids).map((id) => itemById.get(id));
      })
      .filter(Boolean);
  }

  function getOrderedCarbonChain(componentItems, componentBonds) {
    const carbons = getCarbonItems(componentItems);
    const carbonIds = new Set(carbons.map((item) => item.id));
    const adjacency = new Map(carbons.map((item) => [item.id, new Set()]));

    componentBonds.forEach((bond) => {
      if (!carbonIds.has(bond.parentId) || !carbonIds.has(bond.childId)) return;

      adjacency.get(bond.parentId).add(bond.childId);
      adjacency.get(bond.childId).add(bond.parentId);
    });

    const endpoints = carbons.filter((carbon) => adjacency.get(carbon.id).size <= 1);
    const start = endpoints[0] || [...carbons].sort((a, b) => a.x - b.x)[0];

    if (!start) return [];

    const ordered = [];
    const visited = new Set();
    let currentId = start.id;
    let previousId = null;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      ordered.push(carbons.find((carbon) => carbon.id === currentId));

      const nextId = Array.from(adjacency.get(currentId) || []).find(
        (id) => id !== previousId && !visited.has(id)
      );

      previousId = currentId;
      currentId = nextId;
    }

    return ordered;
  }

  function getCarbonSegment(carbon, componentItems, componentBonds) {
    const componentHasHydrogen = componentItems.some((item) => item.text === "H");

    const attachedHydrogens = componentBonds.filter((bond) => {
      const otherId =
        bond.parentId === carbon.id
          ? bond.childId
          : bond.childId === carbon.id
          ? bond.parentId
          : null;

      const other = componentItems.find((item) => item.id === otherId);

      return other?.text === "H";
    }).length;

    const hydrogenCount = componentHasHydrogen
      ? attachedHydrogens
      : Math.max(
          0,
          carbon.spokes -
            componentBonds.filter(
              (bond) => bond.parentId === carbon.id || bond.childId === carbon.id
            ).length
        );

    if (hydrogenCount === 0) return "C";
    if (hydrogenCount === 1) return "CH";

    return `CH${hydrogenCount}`;
  }

  getConnectedComponents(remainingItems, remainingBonds).forEach((componentItems, index) => {
    const componentIds = new Set(componentItems.map((item) => item.id));
    const componentBonds = remainingBonds.filter(
      (bond) => componentIds.has(bond.parentId) && componentIds.has(bond.childId)
    );

    const carbonChain = getOrderedCarbonChain(componentItems, componentBonds);

    const label = getMolecularFormula(componentItems);

    const box = getBox(componentItems);
    const width = Math.max(180, label.length * 18 + 44);
    const height = 76;

const carbonAnchorStart = 34;
const carbonAnchorEnd = width - 34;

const sourcePoints = componentItems.map((item) => {
  const carbonIndex = carbonChain.findIndex((carbon) => carbon.id === item.id);

  if (carbonIndex !== -1) {
    const x =
      carbonChain.length === 1
        ? width / 2
        : carbonAnchorStart +
          (carbonIndex / (carbonChain.length - 1)) *
            (carbonAnchorEnd - carbonAnchorStart);

    return {
      id: item.id,
      x,
      y: height / 2,
    };
  }

  return {
    id: item.id,
    x: width / 2,
    y: height / 2,
  };
});

    summaries.push({
      id: `compact-chain-${index}`,
      compactType: "chain",
      label,
      text: label,
      color: "#334155",
      width,
      height,
      x: Math.max(0, box.centerX - width / 2),
      y: Math.max(0, box.centerY - height / 2),
      sourceIds: componentItems.map((item) => item.id),
      sourcePoints,
    });
  });

  return summaries.sort((a, b) => a.x - b.x);
}

function compactAtoms() {
  if (items.length === 0) return;

  function getUniqueNeighbors(itemId, allBonds) {
    return Array.from(
      new Set(
        allBonds
          .filter((bond) => bond.parentId === itemId || bond.childId === itemId)
          .map((bond) =>
            bond.parentId === itemId ? bond.childId : bond.parentId
          )
      )
    );
  }

  function getComponents() {
    const itemById = new Map(items.map((item) => [item.id, item]));
    const seen = new Set();

    return items
      .map((startItem) => {
        if (seen.has(startItem.id)) return null;

        const queue = [startItem.id];
        const ids = new Set([startItem.id]);
        seen.add(startItem.id);

        while (queue.length > 0) {
          const currentId = queue.shift();

          getUniqueNeighbors(currentId, bonds).forEach((neighborId) => {
            if (!itemById.has(neighborId) || seen.has(neighborId)) return;

            seen.add(neighborId);
            ids.add(neighborId);
            queue.push(neighborId);
          });
        }

        return Array.from(ids).map((id) => itemById.get(id));
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          Math.min(...a.map((item) => item.x)) -
          Math.min(...b.map((item) => item.x))
      );
  }

  function getBox(componentItems) {
    const left = Math.min(...componentItems.map((item) => item.x));
    const top = Math.min(...componentItems.map((item) => item.y));
    const right = Math.max(...componentItems.map((item) => item.x + item.width));
    const bottom = Math.max(...componentItems.map((item) => item.y + item.height));

    return {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }

  const compactScale = 0.75;
  const componentGap = 90;
  const startX = 100;
  const startY = 120;

  const nextPositions = new Map();
  let cursorX = startX;

  getComponents().forEach((componentItems) => {
    const box = getBox(componentItems);

    const compactWidth = Math.max(ATOM_SIZE, box.width * compactScale);
    const compactHeight = Math.max(ATOM_SIZE, box.height * compactScale);

    componentItems.forEach((item) => {
      nextPositions.set(item.id, {
        x: cursorX + (item.x - box.left) * compactScale,
        y: startY + (item.y - box.top) * compactScale,
      });
    });

    cursorX += compactWidth + componentGap;
  });

  const compactedItems = items.map((item) => {
    const position = nextPositions.get(item.id);

    if (!position) return item;

    return {
      ...item,
      x: Math.max(0, position.x),
      y: Math.max(0, position.y),
    };
  });

  setViewMode("compact");
  commitState(compactedItems, bonds);
}

function getNeighbors(itemId, allBonds) {
  return allBonds
    .filter((bond) => bond.parentId === itemId || bond.childId === itemId)
    .map((bond) => (bond.parentId === itemId ? bond.childId : bond.parentId));
}

function getCarbonItems(allItems) {
  return allItems.filter((item) => item.label === "C" || item.text === "C");
}

function carbonFormsRing(carbonItems, allBonds) {
  if (carbonItems.length < 3) return false;

  const carbonIds = new Set(carbonItems.map((item) => item.id));
  const uniqueCarbonEdges = new Set();
  const carbonDegree = new Map();

  carbonItems.forEach((item) => {
    carbonDegree.set(item.id, 0);
  });

  allBonds.forEach((bond) => {
    const isCarbonToCarbon =
      carbonIds.has(bond.parentId) && carbonIds.has(bond.childId);

    if (!isCarbonToCarbon) return;

    const edgeKey = [bond.parentId, bond.childId].sort().join("__");

    if (!uniqueCarbonEdges.has(edgeKey)) {
      uniqueCarbonEdges.add(edgeKey);
      carbonDegree.set(bond.parentId, carbonDegree.get(bond.parentId) + 1);
      carbonDegree.set(bond.childId, carbonDegree.get(bond.childId) + 1);
    }
  });

  const everyCarbonHasTwoCarbonNeighbors = carbonItems.every(
    (item) => carbonDegree.get(item.id) >= 2
  );

  return (
    uniqueCarbonEdges.size >= carbonItems.length &&
    everyCarbonHasTwoCarbonNeighbors
  );
}

function alignAtoms() {
  if (!paletteRef.current || items.length === 0) return;

  const palette = paletteRef.current;
  const nextPositions = new Map();
const carbonSpacing = 88;
const slotDistance = 88;
const componentGap = 120;
const ringRadius = 84;
const attachmentRadius = 145;
const ringGap = 105;
const ringBlockWidth = attachmentRadius * 2 + 80;
const centerY = Math.max(palette.scrollTop + palette.clientHeight / 2, 230);

function getBenzeneGroups(carbons) {
  const groups = new Map();

  carbons
    .filter((item) => item.benzeneIndex !== undefined)
    .forEach((item) => {
      const key = item.moleculeId || "benzene";
      const group = groups.get(key) || [];
      group.push(item);
      groups.set(key, group);
    });

  return Array.from(groups.values())
    .map((group) => group.sort((a, b) => a.benzeneIndex - b.benzeneIndex))
    .sort(
      (a, b) =>
        Math.min(...a.map((item) => item.x)) -
        Math.min(...b.map((item) => item.x))
    );
}

  function getUniqueNeighbors(itemId, allBonds) {
    return Array.from(
      new Set(
        allBonds
          .filter((bond) => bond.parentId === itemId || bond.childId === itemId)
          .map((bond) =>
            bond.parentId === itemId ? bond.childId : bond.parentId
          )
      )
    );
  }

  function getComponents() {
    const itemById = new Map(items.map((item) => [item.id, item]));
    const seen = new Set();

    return items
      .map((startItem) => {
        if (seen.has(startItem.id)) return null;

        const queue = [startItem.id];
        const ids = new Set([startItem.id]);
        seen.add(startItem.id);

        while (queue.length > 0) {
          const currentId = queue.shift();

          getUniqueNeighbors(currentId, bonds).forEach((neighborId) => {
            if (!itemById.has(neighborId) || seen.has(neighborId)) return;

            seen.add(neighborId);
            ids.add(neighborId);
            queue.push(neighborId);
          });
        }

        const componentItems = Array.from(ids).map((id) => itemById.get(id));
        const componentBonds = bonds.filter(
          (bond) => ids.has(bond.parentId) && ids.has(bond.childId)
        );

        return { items: componentItems, bonds: componentBonds };
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          Math.min(...a.items.map((item) => item.x)) -
          Math.min(...b.items.map((item) => item.x))
      );
  }

  function boxesOverlap(a, b, gap = 18) {
    return !(
      a.x + a.width + gap < b.x ||
      b.x + b.width + gap < a.x ||
      a.y + a.height + gap < b.y ||
      b.y + b.height + gap < a.y
    );
  }

  function isPositionFree(candidateBox) {
    for (const [placedId, placedPosition] of nextPositions.entries()) {
      const placedItem = items.find((item) => item.id === placedId);
      if (!placedItem) continue;

      if (
        boxesOverlap(candidateBox, {
          x: placedPosition.x,
          y: placedPosition.y,
          width: placedItem.width,
          height: placedItem.height,
        })
      ) {
        return false;
      }
    }

    return true;
  }

  function findOpenSlot(parentCenter, child, slots) {
    for (let ring = 1; ring <= 6; ring += 1) {
      for (const slot of slots) {
        const position = {
          x: parentCenter.x + slot.dx * ring - child.width / 2,
          y: parentCenter.y + slot.dy * ring - child.height / 2,
        };

        if (isPositionFree({ ...position, width: child.width, height: child.height })) {
          return position;
        }
      }
    }

    return null;
  }

  function placeBranches(parentId, componentItems, componentBonds, placedIds, getSlots) {
    const parent = componentItems.find((item) => item.id === parentId);
    const parentPosition = nextPositions.get(parentId);
    if (!parent || !parentPosition) return;

    const parentCenter = {
      x: parentPosition.x + parent.width / 2,
      y: parentPosition.y + parent.height / 2,
    };

    const children = getUniqueNeighbors(parentId, componentBonds)
      .map((id) => componentItems.find((item) => item.id === id))
      .filter((item) => item && !placedIds.has(item.id));

    children.forEach((child) => {
      const position =
        findOpenSlot(parentCenter, child, getSlots(parentId)) || {
          x: parentCenter.x - child.width / 2,
          y: parentCenter.y + 110 - child.height / 2,
        };

      nextPositions.set(child.id, position);
      placedIds.add(child.id);
      placeBranches(child.id, componentItems, componentBonds, placedIds, getSlots);
    });
  }

  const components = getComponents();

const componentWidths = components.map((component) => {
  const carbons = getCarbonItems(component.items);
  const benzeneGroups = getBenzeneGroups(carbons);

  if (benzeneGroups.length > 0) {
    return (
      benzeneGroups.length * ringBlockWidth +
      Math.max(0, benzeneGroups.length - 1) * ringGap
    );
  }

  if (carbonFormsRing(carbons, component.bonds)) {
    return ringBlockWidth;
  }

  const baseCount = Math.max(carbons.length, 1);
  return Math.max((baseCount - 1) * carbonSpacing + 480, 560);
});

  const totalWidth =
    componentWidths.reduce((sum, width) => sum + width, 0) +
    Math.max(0, components.length - 1) * componentGap;

  let cursorX = Math.max(
    120,
    palette.scrollLeft + palette.clientWidth / 2 - totalWidth / 2
  );

  components.forEach((component, componentIndex) => {
    const componentItems = component.items;
    const componentBonds = component.bonds;
    const componentCenterX = cursorX + componentWidths[componentIndex] / 2;
    cursorX += componentWidths[componentIndex] + componentGap;

    const carbonItems = getCarbonItems(componentItems);
   const benzeneGroups = getBenzeneGroups(carbonItems);
const ringGroups =
  benzeneGroups.length > 0
    ? benzeneGroups
    : carbonFormsRing(carbonItems, componentBonds)
    ? [carbonItems]
    : [];

const isRing = ringGroups.length > 0;

if (isRing) {
  const allRingIds = new Set();

  ringGroups.forEach((group) => {
    group.forEach((item) => allRingIds.add(item.id));
  });

  const placedIds = new Set();
  const ringTotalWidth =
    ringGroups.length * ringBlockWidth +
    Math.max(0, ringGroups.length - 1) * ringGap;

  let ringCursorX = componentCenterX - ringTotalWidth / 2;

  ringGroups.forEach((ringCarbons) => {
    const ringCenterX = ringCursorX + ringBlockWidth / 2;
    ringCursorX += ringBlockWidth + ringGap;

    ringCarbons.forEach((carbon, index) => {
      const angle = (index / ringCarbons.length) * Math.PI * 2 - Math.PI / 2;

      nextPositions.set(carbon.id, {
        x: ringCenterX + Math.cos(angle) * ringRadius - carbon.width / 2,
        y: centerY + Math.sin(angle) * ringRadius - carbon.height / 2,
      });

      placedIds.add(carbon.id);
    });

    ringCarbons.forEach((carbon) => {
      const carbonPosition = nextPositions.get(carbon.id);
      if (!carbonPosition) return;

      const carbonCenter = {
        x: carbonPosition.x + carbon.width / 2,
        y: carbonPosition.y + carbon.height / 2,
      };

      const outwardAngle = Math.atan2(
        carbonCenter.y - centerY,
        carbonCenter.x - ringCenterX
      );

      getUniqueNeighbors(carbon.id, componentBonds)
        .map((id) => componentItems.find((item) => item.id === id))
        .filter(
          (item) => item && !allRingIds.has(item.id) && !placedIds.has(item.id)
        )
        .forEach((child, index) => {
          const radius = attachmentRadius + index * 40;

          nextPositions.set(child.id, {
            x: ringCenterX + Math.cos(outwardAngle) * radius - child.width / 2,
            y: centerY + Math.sin(outwardAngle) * radius - child.height / 2,
          });

          placedIds.add(child.id);
        });
    });
  });

  const allSlots = () => [
    { dx: -slotDistance, dy: 0 },
    { dx: 0, dy: -slotDistance },
    { dx: 0, dy: slotDistance },
    { dx: slotDistance, dy: 0 },
  ];

  Array.from(placedIds).forEach((id) => {
    if (!allRingIds.has(id)) {
      placeBranches(id, componentItems, componentBonds, placedIds, allSlots);
    }
  });

  return;
}

    const baseItems =
      carbonItems.length > 0 ? carbonItems : getRootItems(componentItems, componentBonds);

    const chainWidth = Math.max((baseItems.length - 1) * carbonSpacing, 0);
    const startX = componentCenterX - chainWidth / 2;
    const placedIds = new Set();

    baseItems.forEach((item, index) => {
      nextPositions.set(item.id, {
        x: startX + index * carbonSpacing - item.width / 2,
        y: centerY - item.height / 2,
      });

      placedIds.add(item.id);
    });

    function getChainSlots(parentId) {
      const index = baseItems.findIndex((item) => item.id === parentId);
      const isChainAtom = index !== -1;
      const slots = [];

      if (!isChainAtom || index === 0) {
        slots.push({ dx: -slotDistance, dy: 0 });
      }

      slots.push({ dx: 0, dy: -slotDistance });
      slots.push({ dx: 0, dy: slotDistance });

      if (!isChainAtom || index === baseItems.length - 1) {
        slots.push({ dx: slotDistance, dy: 0 });
      }

      return slots;
    }

    baseItems.forEach((item) => {
      placeBranches(item.id, componentItems, componentBonds, placedIds, getChainSlots);
    });
  });

  const alignedItems = items.map((item) => {
    const position = nextPositions.get(item.id);

    if (!position) return item;

    return {
      ...item,
      x: Math.max(0, position.x),
      y: Math.max(0, position.y),
    };
  });

  setViewMode("expanded");
  commitState(alignedItems, bonds);
}



  useEffect(() => {
    function handlePointerMove(event) {
      if (!dragging) return;

      setDragging((current) => ({
        ...current,
        pointerX: event.clientX,
        pointerY: event.clientY,
      }));
    }

    function handlePointerUp(event) {
      if (!dragging || !paletteRef.current) return;

const palette = paletteRef.current;
const paletteRect = palette.getBoundingClientRect();

const droppedInside =
  event.clientX >= paletteRect.left &&
  event.clientX <= paletteRect.right &&
  event.clientY >= paletteRect.top &&
  event.clientY <= paletteRect.bottom;

      if (!droppedInside) {
        setDragging(null);
        return;
      }

const itemWidth = dragging.element?.width || dragging.item?.width || 0;
const itemHeight = dragging.element?.height || dragging.item?.height || 0;

const rawDropX =
  event.clientX - paletteRect.left + palette.scrollLeft - dragging.offsetX;

const rawDropY =
  event.clientY - paletteRect.top + palette.scrollTop - dragging.offsetY;

const canvasWidth =
  typeof canvasSize.width === "number"
    ? canvasSize.width
    : palette.clientWidth;

const canvasHeight =
  typeof canvasSize.height === "number"
    ? canvasSize.height
    : palette.clientHeight;

const dropX = clamp(rawDropX, 0, canvasWidth - itemWidth);
const dropY = clamp(rawDropY, 0, canvasHeight - itemHeight);

if (dragging.source === "compact") {
  const deltaX = dropX - dragging.item.x;
  const deltaY = dropY - dragging.item.y;
  const sourceIds = new Set(dragging.item.sourceIds || []);

  const nextItems = items.map((item) =>
    sourceIds.has(item.id)
      ? {
          ...item,
          x: Math.max(0, item.x + deltaX),
          y: Math.max(0, item.y + deltaY),
        }
      : item
  );

  commitState(nextItems, bonds);
  setDragging(null);
  return;
}

      const incomingItem =
        dragging.source === "sidebar"
          ? {
              id: crypto.randomUUID(),
              ...dragging.element,
              x: dropX,
              y: dropY,
            }
          : {
              ...dragging.item,
              x: dropX,
              y: dropY,
            };

      const incomingCenter = getCenter(incomingItem);

      const possibleTargets = items
        .filter((item) => item.id !== incomingItem.id)
        .map((item) => ({
          item,
          distance: getDistance(incomingCenter, getCenter(item)),
        }))
        .filter(({ distance }) => distance <= CONNECT_DISTANCE)
        .sort((a, b) => a.distance - b.distance);

      const target = possibleTargets[0]?.item;

      let finalItem = incomingItem;
      let newBond = null;

      if (target) {
        const parentFreeAngle = getFirstFreeAngle(target, bonds);
        const childFreeAngle = getFirstFreeAngle(incomingItem, bonds);

        if (parentFreeAngle !== undefined && childFreeAngle !== undefined) {
          const parentCenter = getCenter(target);
          const parentVector = angleToVector(parentFreeAngle);

          const finalCenter = {
            x: parentCenter.x + parentVector.x * BOND_LENGTH,
            y: parentCenter.y + parentVector.y * BOND_LENGTH,
          };

          finalItem = {
            ...incomingItem,
            x: finalCenter.x - incomingItem.width / 2,
            y: finalCenter.y - incomingItem.height / 2,
          };

            newBond = {
            id: crypto.randomUUID(),
            parentId: target.id,
            childId: finalItem.id,
            parentAngle: parentFreeAngle,
            childAngle: childFreeAngle,
            };
        }
      }

let nextItems = items;
let nextBonds = bonds;

if (dragging.source === "sidebar") {
  nextItems = [...items, finalItem];
}

if (dragging.source === "palette") {
  nextItems = items.map((item) =>
    item.id === finalItem.id ? finalItem : item
  );
}

if (newBond) {
  nextBonds = [...bonds, newBond];
}

commitState(nextItems, nextBonds);

      setDragging(null);
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [dragging, items, bonds]);

  const ghost =
    dragging?.source === "sidebar"
      ? dragging.element
      : dragging?.source === "palette" || dragging?.source === "compact"
      ? dragging.item
      : null;

      const visibleItems = viewMode === "compact" ? getCompactItems() : items;

function getCompactBonds(compactItems) {
  const compactOwnerByAtomId = new Map();

  compactItems.forEach((compactItem) => {
    compactItem.sourceIds?.forEach((sourceId) => {
      compactOwnerByAtomId.set(sourceId, compactItem.id);
    });
  });

  const compactBondKeys = new Set();
  const compactBonds = [];

  bonds.forEach((bond) => {
    const parentCompactId = compactOwnerByAtomId.get(bond.parentId);
    const childCompactId = compactOwnerByAtomId.get(bond.childId);

    if (!parentCompactId || !childCompactId) return;
    if (parentCompactId === childCompactId) return;

    const key = [parentCompactId, childCompactId, bond.parentId, bond.childId]
      .sort()
      .join("__");

    if (compactBondKeys.has(key)) return;
    compactBondKeys.add(key);

    const from = compactItems.find((item) => item.id === parentCompactId);
    const to = compactItems.find((item) => item.id === childCompactId);

    const fromPoint = from?.sourcePoints?.find((point) => point.id === bond.parentId);
    const toPoint = to?.sourcePoints?.find((point) => point.id === bond.childId);

    if (!from || !to || !fromPoint || !toPoint) return;

    compactBonds.push({
      id: key,
      from: {
        x: from.x + fromPoint.x,
        y: from.y + fromPoint.y,
      },
      to: {
        x: to.x + toPoint.x,
        y: to.y + toPoint.y,
      },
    });
  });

  return compactBonds;
}

const compactBonds =
  viewMode === "compact" ? getCompactBonds(visibleItems) : [];

      const canvasSize = getCanvasSize();

  async function fetchAIResponse(prompt) {
  const callGemini = httpsCallable(functions, "callGemini");

  try {
    const result = await callGemini({
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      generationConfig: {
        maxOutputTokens: 8192,
        temperature: 0.2,
      },
    });

    return result.data.candidates?.[0]?.content?.parts?.[0]?.text;
  } catch (err) {
    console.error("Gemini error:", err.message);
    alert("AI is busy right now. Please try again in a couple of minutes.");
    return null;
  }
}

function parseGeminiJson(text) {
  if (!text) return null;

  const cleaned = text
    .replace(/```json/g, "")
    .replace(/```/g, "")
    .trim();

  return JSON.parse(cleaned);
}

function getElementTemplateBySymbol(symbol) {
  const normalized = String(symbol || "").trim().toLowerCase();

  const symbolMap = {
    c: "C",
    carbon: "C",
    h: "H",
    hydrogen: "H",
    o: "O",
    oxygen: "O",
    n: "N",
    nitrogen: "N",
    cl: "Cl",
    chlorine: "Cl",
  };

  const finalSymbol = symbolMap[normalized];

  return sidebarElements.find(
    (element) => element.label === finalSymbol || element.text === finalSymbol
  );
}

function getAngleDifference(a, b) {
  return Math.abs((((a - b + 180) % 360) + 360) % 360 - 180);
}

function layoutGeneratedMolecule(usableAtoms, cleanBonds) {
  const positions = new Map();
  const atomBySourceId = new Map(
    usableAtoms.map((atom) => [atom.sourceId, atom])
  );

  const heavyAtoms = usableAtoms.filter((atom) => atom.template.text !== "H");
  const hydrogenAtoms = usableAtoms.filter((atom) => atom.template.text === "H");

  if (heavyAtoms.length === 0) {
    usableAtoms.forEach((atom, index) => {
      positions.set(atom.sourceId, { x: index, y: 0 });
    });

    return positions;
  }

  const heavyIds = new Set(heavyAtoms.map((atom) => atom.sourceId));

  const heavyAdjacency = new Map(
    heavyAtoms.map((atom) => [atom.sourceId, []])
  );

  cleanBonds.forEach((bond) => {
    if (!heavyIds.has(bond.from) || !heavyIds.has(bond.to)) return;

    heavyAdjacency.get(bond.from).push(bond.to);
    heavyAdjacency.get(bond.to).push(bond.from);
  });

  const heavyEdgeKeys = new Set();

  cleanBonds.forEach((bond) => {
    if (!heavyIds.has(bond.from) || !heavyIds.has(bond.to)) return;
    heavyEdgeKeys.add([bond.from, bond.to].sort().join("__"));
  });

  const isRing =
    heavyAtoms.length >= 3 &&
    heavyEdgeKeys.size >= heavyAtoms.length &&
    heavyAtoms.every(
      (atom) => new Set(heavyAdjacency.get(atom.sourceId) || []).size >= 2
    );

  function getRingOrder() {
    const startId = heavyAtoms[0].sourceId;
    const visited = new Set();
    const ordered = [];

    let currentId = startId;
    let previousId = null;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      ordered.push(currentId);

      const nextId = (heavyAdjacency.get(currentId) || []).find(
        (id) => id !== previousId && !visited.has(id)
      );

      previousId = currentId;
      currentId = nextId;
    }

    return ordered.length === heavyAtoms.length
      ? ordered
      : heavyAtoms.map((atom) => atom.sourceId);
  }

  function getLongestHeavyPath() {
    function bfsPath(startId) {
      const queue = [startId];
      const seen = new Set([startId]);
      const parent = new Map();
      let farthestId = startId;

      while (queue.length > 0) {
        const currentId = queue.shift();
        farthestId = currentId;

        (heavyAdjacency.get(currentId) || []).forEach((neighborId) => {
          if (seen.has(neighborId)) return;

          seen.add(neighborId);
          parent.set(neighborId, currentId);
          queue.push(neighborId);
        });
      }

      const path = [];
      let cursor = farthestId;

      while (cursor) {
        path.unshift(cursor);
        cursor = parent.get(cursor);
      }

      return path;
    }

    let bestPath = [];

    heavyAtoms.forEach((atom) => {
      const path = bfsPath(atom.sourceId);
      if (path.length > bestPath.length) bestPath = path;
    });

    return bestPath.length > 0
      ? bestPath
      : heavyAtoms.map((atom) => atom.sourceId);
  }

  if (isRing) {
    const ringOrder = getRingOrder();
    const radius = Math.max(1.35, ringOrder.length * 0.24);

    ringOrder.forEach((id, index) => {
      const angle = (index / ringOrder.length) * Math.PI * 2 - Math.PI / 2;

      positions.set(id, {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      });
    });
  } else {
    const mainPath = getLongestHeavyPath();
    const spacing = 1.15;
    const startX = -((mainPath.length - 1) * spacing) / 2;

    mainPath.forEach((id, index) => {
      positions.set(id, {
        x: startX + index * spacing,
        y: index % 2 === 0 ? 0 : 0.25,
      });
    });

    function getPlacedNeighborAngles(parentId) {
      const parentPosition = positions.get(parentId);
      if (!parentPosition) return [];

      return (heavyAdjacency.get(parentId) || [])
        .filter((id) => positions.has(id))
        .map((id) => {
          const neighborPosition = positions.get(id);

          return (
            Math.atan2(
              neighborPosition.y - parentPosition.y,
              neighborPosition.x - parentPosition.x
            ) *
            (180 / Math.PI)
          );
        });
    }

    function pickBranchAngle(parentId, branchIndex) {
      const usedAngles = getPlacedNeighborAngles(parentId);
      const candidateAngles = [-90, 90, 0, 180, -45, 45, -135, 135];

      const rankedAngles = candidateAngles
        .map((angle) => ({
          angle,
          score: usedAngles.reduce(
            (score, usedAngle) =>
              score + getAngleDifference(angle, usedAngle),
            branchIndex * 10
          ),
        }))
        .sort((a, b) => b.score - a.score);

      return rankedAngles[0]?.angle || 90;
    }

    function placeHeavyBranches(parentId) {
      const parentPosition = positions.get(parentId);
      if (!parentPosition) return;

      const unplacedNeighbors = (heavyAdjacency.get(parentId) || []).filter(
        (id) => !positions.has(id)
      );

      unplacedNeighbors.forEach((neighborId, index) => {
        const angle = pickBranchAngle(parentId, index);
        const vector = angleToVector(angle);

        positions.set(neighborId, {
          x: parentPosition.x + vector.x * 1.15,
          y: parentPosition.y + vector.y * 1.15,
        });

        placeHeavyBranches(neighborId);
      });
    }

    mainPath.forEach((id) => placeHeavyBranches(id));
  }

  heavyAtoms.forEach((atom, index) => {
    if (positions.has(atom.sourceId)) return;

    positions.set(atom.sourceId, {
      x: index * 1.3,
      y: 1.6,
    });
  });

  const reservedAnglesById = new Map(
    usableAtoms.map((atom) => [atom.sourceId, []])
  );

  function takeSpoke(atomId, desiredAngle) {
    const atom = atomBySourceId.get(atomId);
    const usedAngles = reservedAnglesById.get(atomId);
    const angles = spokeAngles[atom.template.spokes] || [0];

    const availableAngles = angles.filter((angle) => !usedAngles.includes(angle));

    const chosenAngle =
      availableAngles.length > 0
        ? availableAngles.reduce((best, angle) =>
            getAngleDifference(angle, desiredAngle) <
            getAngleDifference(best, desiredAngle)
              ? angle
              : best
          )
        : desiredAngle;

    usedAngles.push(chosenAngle);
    return chosenAngle;
  }

  cleanBonds.forEach((bond) => {
    if (!heavyIds.has(bond.from) || !heavyIds.has(bond.to)) return;

    const fromPosition = positions.get(bond.from);
    const toPosition = positions.get(bond.to);

    if (!fromPosition || !toPosition) return;

    const fromAngle =
      Math.atan2(toPosition.y - fromPosition.y, toPosition.x - fromPosition.x) *
      (180 / Math.PI);

    const toAngle =
      Math.atan2(fromPosition.y - toPosition.y, fromPosition.x - toPosition.x) *
      (180 / Math.PI);

    for (let index = 0; index < bond.order; index += 1) {
      takeSpoke(bond.from, fromAngle);
      takeSpoke(bond.to, toAngle);
    }
  });

  hydrogenAtoms.forEach((hydrogen, index) => {
    const bond = cleanBonds.find(
      (candidate) =>
        candidate.from === hydrogen.sourceId || candidate.to === hydrogen.sourceId
    );

    if (!bond) {
      positions.set(hydrogen.sourceId, {
        x: index,
        y: 2,
      });
      return;
    }

    const parentId = bond.from === hydrogen.sourceId ? bond.to : bond.from;
    const parentPosition = positions.get(parentId);

    if (!parentPosition) {
      positions.set(hydrogen.sourceId, {
        x: index,
        y: 2,
      });
      return;
    }

    const angle = takeSpoke(parentId, -90);
    const vector = angleToVector(angle);

    positions.set(hydrogen.sourceId, {
      x: parentPosition.x + vector.x * 0.95,
      y: parentPosition.y + vector.y * 0.95,
    });
  });

  return positions;
}

function addGeneratedMoleculeToPalette(molecule) {
  if (!paletteRef.current) return;

  const sourceAtoms = Array.isArray(molecule.atoms) ? molecule.atoms : [];
  const sourceBonds = Array.isArray(molecule.bonds) ? molecule.bonds : [];

  const usableAtoms = sourceAtoms
    .map((atom, index) => ({
      ...atom,
      sourceId: String(atom.id || `a${index + 1}`),
      template: getElementTemplateBySymbol(atom.element),
    }))
    .filter((atom) => atom.template);

  if (usableAtoms.length === 0) {
    alert("No supported atoms were returned for this molecule.");
    return;
  }

  const atomBySourceId = new Map(
    usableAtoms.map((atom) => [atom.sourceId, atom])
  );

  const validSourceIds = new Set(usableAtoms.map((atom) => atom.sourceId));

  const cleanBonds = sourceBonds
    .map((bond) => ({
      from: String(bond.from),
      to: String(bond.to),
      order: clamp(Number(bond.order) || 1, 1, 3),
    }))
    .filter(
      (bond) =>
        validSourceIds.has(bond.from) &&
        validSourceIds.has(bond.to) &&
        bond.from !== bond.to
    );

  const localPositions = layoutGeneratedMolecule(usableAtoms, cleanBonds);

  const palette = paletteRef.current;
  const scale = 88;

  const minX = Math.min(
    ...usableAtoms.map((atom) => localPositions.get(atom.sourceId)?.x || 0)
  );
  const minY = Math.min(
    ...usableAtoms.map((atom) => localPositions.get(atom.sourceId)?.y || 0)
  );
  const maxX = Math.max(
    ...usableAtoms.map((atom) => localPositions.get(atom.sourceId)?.x || 0)
  );
  const maxY = Math.max(
    ...usableAtoms.map((atom) => localPositions.get(atom.sourceId)?.y || 0)
  );

  const moleculeWidth = Math.max(ATOM_SIZE, (maxX - minX) * scale + ATOM_SIZE);
  const moleculeHeight = Math.max(ATOM_SIZE, (maxY - minY) * scale + ATOM_SIZE);

  const startX =
    items.length === 0
      ? Math.max(
          120,
          palette.scrollLeft + palette.clientWidth / 2 - moleculeWidth / 2
        )
      : getNextMoleculeStartX(180);

  const startY = Math.max(
    140,
    palette.scrollTop + palette.clientHeight / 2 - moleculeHeight / 2
  );

  const generatedIdBySourceId = new Map();

  const generatedItems = usableAtoms.map((atom) => {
    const id = crypto.randomUUID();
    const position = localPositions.get(atom.sourceId) || { x: 0, y: 0 };

    generatedIdBySourceId.set(atom.sourceId, id);

    return {
      id,
      ...atom.template,
      x: startX + (position.x - minX) * scale,
      y: startY + (position.y - minY) * scale,
    };
  });

  const generatedItemById = new Map(
    generatedItems.map((item) => [item.id, item])
  );

  const usedAnglesById = new Map(
    generatedItems.map((item) => [item.id, []])
  );

  function takeClosestFreeAngle(item, target) {
    const itemCenter = getCenter(item);
    const targetCenter = getCenter(target);

    const desiredAngle =
      Math.atan2(targetCenter.y - itemCenter.y, targetCenter.x - itemCenter.x) *
      (180 / Math.PI);

    const usedAngles = usedAnglesById.get(item.id);
    const availableAngles = (spokeAngles[item.spokes] || [0]).filter(
      (angle) => !usedAngles.includes(angle)
    );

    const chosenAngle =
      availableAngles.length > 0
        ? availableAngles.reduce((best, angle) =>
            getAngleDifference(angle, desiredAngle) <
            getAngleDifference(best, desiredAngle)
              ? angle
              : best
          )
        : 0;

    usedAngles.push(chosenAngle);
    return chosenAngle;
  }

  const generatedBonds = [];

  cleanBonds.forEach((bond) => {
    const parentId = generatedIdBySourceId.get(bond.from);
    const childId = generatedIdBySourceId.get(bond.to);

    if (!parentId || !childId) return;

    const parent = generatedItemById.get(parentId);
    const child = generatedItemById.get(childId);

    for (let index = 0; index < bond.order; index += 1) {
      generatedBonds.push({
        id: crypto.randomUUID(),
        parentId,
        childId,
        parentAngle: takeClosestFreeAngle(parent, child),
        childAngle: takeClosestFreeAngle(child, parent),
      });
    }
  });

  setViewMode("expanded");

  commitState(
    [...items, ...generatedItems],
    [...bonds, ...generatedBonds]
  );
}

async function handleMoleculeSubmit() {
  const name = moleculeName.trim();

  if (!name) {
    alert("Please enter a molecule name.");
    return;
  }

  setIsGeneratingMolecule(true);
  setMoleculeError("");

const prompt = `
Return ONLY valid JSON. No markdown. No explanation.

Create the correct molecular structure graph for:
"${name}"

Supported atoms only:
C, H, O, N, Cl

Do NOT return x or y coordinates.
We will draw the molecule layout in our app.

Return JSON exactly like this:
{
  "name": "molecule name",
  "formula": "chemical formula",
  "atoms": [
    {
      "id": "a1",
      "element": "C",
      "name": "carbon"
    }
  ],
  "bonds": [
    {
      "from": "a1",
      "to": "a2",
      "order": 1
    }
  ]
}

Rules:
- Include every atom explicitly, including all hydrogen atoms.
- Carbon valency must be exactly 4.
- Oxygen valency must normally be 2.
- Nitrogen valency must normally be 3.
- Hydrogen valency must be exactly 1.
- Chlorine valency must be exactly 1.
- Bond order counts toward valency.
- For benzene, use six carbon atoms in a ring with alternating double bonds and one hydrogen on each carbon.
- For alcohols, include C-O-H.
- For carboxylic acids, include C(=O)-O-H.
- For aldehydes, include C(=O)-H.
- For ketones, include C(=O)-C.
- Return only atoms and bonds. No coordinates.
`;

  try {
    const content = await fetchAIResponse(prompt);

    if (!content) {
      setMoleculeError("AI did not return a response.");
      return;
    }

    const molecule = parseGeminiJson(content);
    addGeneratedMoleculeToPalette(molecule);
  } catch (err) {
    console.error(err);
    setMoleculeError("Could not create molecule. Try a simpler molecule name.");
  } finally {
    setIsGeneratingMolecule(false);
  }
}

  return (
    <div style={styles.page}>
      <aside style={styles.sidebar}>
        <h3 style={styles.sidebarTitle}>Add Atoms or Molecules</h3>

        {sidebarElements.map((element) => (
          <div
            key={element.type}
            onPointerDown={(event) => startSidebarDrag(event, element)}
            style={styles.sidebarItem}
          >
            {element.label}
          </div>
        ))}

        <div style={styles.sidebarTools}>
  <label style={styles.sidebarLabel}>Carbon chain</label>

  <div style={styles.chainControl}>
    <select
      value={carbonChainLength}
      onChange={(event) => setCarbonChainLength(Number(event.target.value))}
      style={styles.chainInput}
    >
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((length) => (
        <option key={length} value={length}>
          {length}
        </option>
      ))}
    </select>

    <button type="button" onClick={addCarbonChain} style={styles.sidebarButton}>
      Add C Chain
    </button>
  </div>

  <button
  type="button"
  onClick={addBenzeneMolecule}
  style={{
    ...styles.sidebarButton,
    width: "100%",
    marginTop: "12px",
  }}
>
  Add C6H6 Benzene
</button>

<label
  style={{
    ...styles.sidebarLabel,
    marginTop: "16px",
  }}
>
  Enter Name of Your Molecule
</label>

<input
  type="text"
  value={moleculeName}
  onChange={(event) => setMoleculeName(event.target.value)}
  style={{
    ...styles.chainInput,
    width: "100%",
    boxSizing: "border-box",
    background: "white",
    color: "#111827",
  }}
/>

<button
  type="button"
  onClick={handleMoleculeSubmit}
  disabled={isGeneratingMolecule || !moleculeName.trim()}
  style={{
    ...styles.sidebarButton,
    width: "100%",
    marginTop: "10px",
    opacity: isGeneratingMolecule || !moleculeName.trim() ? 0.45 : 1,
  }}
>
  {isGeneratingMolecule ? "Creating..." : "Submit"}
</button>

{moleculeError && (
  <div style={{ marginTop: "8px", color: "#fecaca", fontSize: "12px" }}>
    {moleculeError}
  </div>
)}

</div>

      </aside>

      <section style={styles.workArea}>

  <main ref={paletteRef} style={styles.palette}>
  <div
  style={{
    ...styles.canvas,
    width: canvasSize.width,
    height: canvasSize.height,
  }}
>
{viewMode === "expanded" &&
  groupBondsByPair(bonds).map((bondGroup) => {
    const firstBond = bondGroup[0];

    const parent = items.find((item) => item.id === firstBond.parentId);
    const child = items.find((item) => item.id === firstBond.childId);

    if (!parent || !child) return null;

    const parentCenter = getCenter(parent);
    const childCenter = getCenter(child);
    const distance = getDistance(parentCenter, childCenter);

    const angleRadians = Math.atan2(
      childCenter.y - parentCenter.y,
      childCenter.x - parentCenter.x
    );

    const angleDegrees = angleRadians * (180 / Math.PI);
    const offsets = getParallelOffsets(bondGroup.length);

const isCarbonToCarbon =
  parent.text === "C" && child.text === "C";

const isDoubleBond = bondGroup.length === 2;

return offsets.map((offset, index) => {
  const offsetX = Math.cos(angleRadians + Math.PI / 2) * offset;
  const offsetY = Math.sin(angleRadians + Math.PI / 2) * offset;

  const shouldDash = isCarbonToCarbon && isDoubleBond && index === 1;

  return (
    <div
      key={`${getBondPairKey(firstBond)}-${index}`}
      style={{
        ...styles.bondLine,
        left: parentCenter.x + offsetX,
        top: parentCenter.y + offsetY,
        width: distance,
        transform: `rotate(${angleDegrees}deg)`,
        background: shouldDash ? "transparent" : styles.bondLine.background,
        borderTop: shouldDash ? "4px dashed #2563eb" : "none",
        height: shouldDash ? 0 : styles.bondLine.height,
      }}
    />
  );
});
  })}



{viewMode === "compact" &&
  compactBonds.map((compactBond) => {
    const distance = getDistance(compactBond.from, compactBond.to);

    const angleDegrees =
      Math.atan2(
        compactBond.to.y - compactBond.from.y,
        compactBond.to.x - compactBond.from.x
      ) *
      (180 / Math.PI);

    return (
      <div
        key={compactBond.id}
        style={{
          ...styles.bondLine,
          left: compactBond.from.x,
          top: compactBond.from.y,
          width: distance,
          transform: `rotate(${angleDegrees}deg)`,
          background: "#111827",
          zIndex: 4,
        }}
      />
    );
  })}
        {visibleItems.map((item) => (
          <div
            key={item.id}
onPointerDown={(event) => {
  startItemDrag(event, item);
}}
          style={{
            ...styles.droppedItem,
            width: item.width,
            height: item.height,
            left: item.x,
            top: item.y,
            background: `${item.color}22`,
            borderColor: item.color,
          }}
          >

{viewMode !== "compact" && (
  <button
    type="button"
    onPointerDown={(event) => event.stopPropagation()}
    onClick={(event) => {
      event.stopPropagation();
      removeAtom(item.id);
    }}
    style={styles.removeAtomButton}
  >
    x
  </button>
)}

{viewMode === "compact" ? (
  <CompactMoleculeNode item={item} />
) : (
  <AtomNode item={item} bonds={bonds} />
)}

          </div>
        ))}
</div>
        </main>

        <div style={styles.buttonArea}>

<div style={styles.bottomBar}>
  <button
    type="button"
    onClick={undoState}
    disabled={history.length === 0}
    style={{
      ...styles.controlButton,
      opacity: history.length === 0 ? 0.45 : 1,
    }}
  >
    Back
  </button>

  <button
    type="button"
    onClick={redoState}
    disabled={future.length === 0}
    style={{
      ...styles.controlButton,
      opacity: future.length === 0 ? 0.45 : 1,
    }}
  >
    Forward
  </button>

  <button
  type="button"
  onClick={compactAtoms}
  disabled={items.length === 0 || viewMode === "compact"}
  style={{
    ...styles.controlButton,
    opacity: items.length === 0 || viewMode === "compact" ? 0.45 : 1,
  }}
>
  Compact
</button>

<button
  type="button"
  onClick={alignAtoms}
  disabled={items.length === 0}
  style={{
    ...styles.controlButton,
    opacity: items.length === 0 ? 0.45 : 1,
  }}
>
  Align
</button>

  <button
    type="button"
    onClick={resetPalette}
    disabled={items.length === 0 && bonds.length === 0}
    style={{
      ...styles.controlButton,
      opacity: items.length === 0 && bonds.length === 0 ? 0.45 : 1,
    }}
  >
    Reset
  </button>
</div>
</div>

      </section>

      {ghost && (
        <div
          style={{
            ...styles.dragGhost,
            width: ghost.width,
            height: ghost.height,
            left: dragging.pointerX - dragging.offsetX,
            top: dragging.pointerY - dragging.offsetY,
          }}
        >
          {dragging.source === "compact" ? (
            <CompactMoleculeNode item={ghost} />
          ) : (
            <AtomNode item={ghost} bonds={[]} />
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    height: "100vh",
    width: "100%",
    background: "#f4f6f8",
    fontFamily: "Arial, sans-serif",
  },

canvas: {
  position: "relative",
  minWidth: "100%",
  minHeight: "100%",
  background: "white",
},

  sidebar: {
    width: "240px",
    padding: "20px",
    background: "#111827",
    color: "white",
  },

  sidebarTitle: {
    margin: "0 0 16px",
    fontSize: "18px",
  },

  sidebarItem: {
    padding: "12px 14px",
    marginBottom: "12px",
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "8px",
    cursor: "grab",
    userSelect: "none",
  },

palette: {
  flex: 1,
  minHeight: 0,
  margin: "24px 24px 0",
  background: "white",
  border: "2px dashed #cbd5e1",
  borderRadius: "12px",
  overflow: "auto",
},

  droppedItem: {
    position: "absolute",
    background: "#e0f2fe",
    border: "1px solid #0284c7",
    borderRadius: "8px",
    cursor: "grab",
    userSelect: "none",
    zIndex: 2,
  },

  workArea: {
  flex: 1,
  minWidth: 0,
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  background: "#f4f6f8",
},

buttonArea: {
  flexShrink: 0,
  padding: "14px 24px 20px",
  background: "#f4f6f8",
},

  dragGhost: {
    position: "fixed",
    background: "#dbeafe",
    border: "1px solid #2563eb",
    borderRadius: "8px",
    opacity: 0.8,
    pointerEvents: "none",
    zIndex: 9999,
    userSelect: "none",
  },

  atomWrap: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

atomCircle: {
  position: "relative",
  zIndex: 2,
  width: "24px",
  height: "24px",
  borderRadius: "50%",
  background: "white",
  border: "2px solid #111827",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "12px",
  color: "#111827",
},

removeAtomButton: {
  position: "absolute",
  right: "2px",
  top: "2px",
  width: "15px",
  height: "15px",
  borderRadius: "50%",
  border: "1px solid #94a3b8",
  background: "white",
  color: "#0f172a",
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: "11px",
  cursor: "pointer",
  zIndex: 5,
},

spoke: {
  position: "absolute",
  left: "50%",
  top: "50%",
  width: "18px",
  height: "2px",
  background: "#111827",
  transformOrigin: "0 50%",
  zIndex: 1,
},

bondLine: {
  position: "absolute",
  height: "2px",
  background: "#2563eb",
  transformOrigin: "0 50%",
  zIndex: 1,
  pointerEvents: "none",
},

bottomBar: {
  display: "flex",
  justifyContent: "center",
  gap: "12px",
  padding: "10px",
  background: "#ffffff",
  border: "1px solid #cbd5e1",
  borderRadius: "10px",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.12)",
},

  controlButton: {
    minWidth: "92px",
    padding: "10px 14px",
    border: "1px solid #94a3b8",
    borderRadius: "8px",
    background: "#f8fafc",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },

  sidebarTools: {
  marginTop: "22px",
  paddingTop: "18px",
  borderTop: "1px solid #374151",
},

sidebarLabel: {
  display: "block",
  marginBottom: "8px",
  fontSize: "13px",
  color: "#cbd5e1",
  fontWeight: 700,
},

chainControl: {
  display: "flex",
  gap: "8px",
  marginBottom: "12px",
},

chainInput: {
  width: "64px",
  padding: "10px",
  border: "1px solid #475569",
  borderRadius: "8px",
  background: "#0f172a",
  color: "white",
  fontWeight: 700,
},

sidebarButton: {
  padding: "10px 12px",
  border: "1px solid #475569",
  borderRadius: "8px",
  background: "#f8fafc",
  color: "#111827",
  fontWeight: 700,
  cursor: "pointer",
},

compactMolecule: {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},

condensedFormula: {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 14px",
  fontSize: "20px",
  fontWeight: 800,
  color: "#111827",
  whiteSpace: "nowrap",
},

};